import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { PropertyService } from '../../services/property.service';

@Component({
  selector: 'app-receipt-view',
  templateUrl: './receipt-view.component.html',
  styleUrls: ['./receipt-view.component.scss']
})
export class ReceiptViewComponent implements OnInit {
  imageUrl: SafeUrl | null;
  propertyId: string | null;
  expenseId: string | null;
  property: any;
  expense: any;
  expenseDate: Date | null = null;
  categories = [
    'ADVERTISING',
    'CAR_AND_TRUCK_EXPENSES',
    'COMMISSIONS_AND_FEES',
    'CONTRACT_LABOR',
    'DEPRECIATION',
    'EMPLOYEE_WAGES',
    'INSURANCE_OTHER_THAN_HEALTH',
    'INTEREST_MORTGAGE_AND_OTHER',
    'LEGAL_AND_PROFESSIONAL_SERVICES',
    'OFFICE_EXPENSES',
    'RENT_OR_LEASE',
    'REPAIRS_AND_MAINTENANCE',
    'SUPPLIES',
    'TAXES_AND_LICENSES',
    'TRAVEL',
    'MEALS',
    'UTILITIES',
    'OTHER_EXPENSES'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private propertyService: PropertyService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    this.propertyId = this.route.snapshot.queryParamMap.get('propertyId');
    this.expenseId = this.route.snapshot.queryParamMap.get('expenseId');
    
    if (this.expenseId) {
      // Load expense directly by ID (works after upload)
      this.propertyService.getExpenseById(this.expenseId).then((expense: any) => {
        this.expense = expense;
        if (this.expense) {
          // Handle both receiptUrl and receipt_url
          const receiptUrl = this.expense.receiptUrl || this.expense.receipt_url;
          if (receiptUrl) {
            this.loadImage(receiptUrl);
          }
          // Initialize date from expense
          if (this.expense.date) {
            this.expenseDate = new Date(this.expense.date);
          } else {
            this.expenseDate = new Date();
          }
        }
        // Also load property if propertyId is provided
        if (this.propertyId) {
          this.propertyService.getProperties().then(props => {
            this.property = props.find(p => p.id === this.propertyId);
          });
        } else if (this.expense && this.expense.propertyId) {
          // Load property from expense if available
          this.propertyId = this.expense.propertyId;
          this.propertyService.getProperties().then(props => {
            this.property = props.find(p => p.id === this.expense.propertyId);
          });
        }
      }).catch((error) => {
        console.error('Error loading expense:', error);
        // Fallback to old method if direct load fails
        if (this.propertyId) {
          this.propertyService.getProperties().then(props => {
            this.property = props.find(p => p.id === this.propertyId);
            if (this.property && this.property.expenses) {
              this.expense = this.property.expenses.find(e => e.id == this.expenseId);
              if (this.expense) {
                const receiptUrl = this.expense.receiptUrl || this.expense.receipt_url;
                this.loadImage(receiptUrl);
                // Initialize date from expense
                if (this.expense.date) {
                  this.expenseDate = new Date(this.expense.date);
                } else {
                  this.expenseDate = new Date();
                }
              }
            }
          });
        }
      });
    } else if (this.propertyId) {
      // Fallback: load from property if no expenseId
      this.propertyService.getProperties().then(props => {
        this.property = props.find(p => p.id === this.propertyId);
      });
    }
  }

  onDateChange(event: any) {
    if (event.value && this.expense) {
      // Update expense date when date picker changes
      this.expense.date = event.value.toISOString();
    }
  }

  save() {
    if (this.expense && this.expense.id) {
      // Ensure date is set from expenseDate if it exists
      if (this.expenseDate) {
        this.expense.date = this.expenseDate.toISOString();
      }
      // Update the expense via the API
      this.propertyService.updateExpense(this.expense).then(() => {
        // Clear property cache to force refresh
        this.propertyService.clearProperties();
        // Navigate back to expenses page
        if (this.propertyId) {
          this.router.navigate(['property-expenses'], { queryParams: { propertyId: this.propertyId } });
        } else {
          this.router.navigate(['property-expenses']);
        }
      }).catch((error) => {
        console.error('Error saving expense:', error);
        // Still navigate back even if there's an error
        if (this.propertyId) {
          this.router.navigate(['property-expenses'], { queryParams: { propertyId: this.propertyId } });
        } else {
          this.router.navigate(['property-expenses']);
        }
      });
    }
  }

  close() {
    if (this.propertyId) {
      this.router.navigate(['property-expenses'], { queryParams: { propertyId: this.propertyId } });
    } else {
      this.router.navigate(['property-expenses']);
    }
  }

  private loadImage(path: string) {
    if (!path) {
      this.imageUrl = null;
      return;
    }

    // If it's already a full URL (S3 or data URL), use it directly
    if (path.startsWith('http') || path.startsWith('data:')) {
      this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(path);
      return;
    }

    // If it's an S3 URL, extract filename and use property-specific endpoint
    if (path.includes('amazonaws.com') || path.includes('s3')) {
      // Extract filename from S3 URL
      // Format: https://bucket.s3.region.amazonaws.com/expense-receipts/filename.jpg
      // or: expense-receipts/filename.jpg
      let fileName = path;
      if (path.includes('/')) {
        fileName = path.substring(path.lastIndexOf('/') + 1);
        // If path contains expense-receipts/, extract just the filename
        if (path.includes('expense-receipts/')) {
          const parts = path.split('expense-receipts/');
          if (parts.length > 1) {
            fileName = parts[1].split('?')[0]; // Remove query params if any
          }
        }
      }
      
      if (this.propertyId) {
        path = `/v1/admin/properties/${this.propertyId}/receipts/${fileName}`;
      } else {
        // Fallback to organization endpoint
        path = `/v1/admin/organization/receipts/${fileName}`;
      }
    }

    // For relative paths, fetch via API
    this.propertyService.getReceiptImage(path).then(blob => {
      const objectUrl = URL.createObjectURL(blob);
      this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
    }).catch((error) => {
      console.error('Error loading receipt image:', error);
      this.imageUrl = null;
    });
  }
}
